import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/shared/lib/api";
import { toast } from "sonner";
import {
  Search, Loader2, ShieldAlert, Sparkles, Phone, Mail, ChevronLeft, ChevronRight,
  CalendarDays, ArrowRight, Crown, UserX, UserCheck, Car, Trash2, AlertTriangle,
} from "lucide-react";

/**
 * /admin/customers — lightweight customer control panel.
 * View all users, search by name/email, filter by founding/active, and toggle
 * is_founding_rider + is_active inline. Founding-rider toggle is the headline
 * action since pricing follows that flag automatically.
 */
const PAGE_SIZE = 50;

export default function AdminCustomers() {
  const [data, setData] = useState({ users: [], total: 0, skip: 0 });
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | founding | non_founding | inactive
  const [skip, setSkip] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // { users: [...] } | null
  const [bulkBusy, setBulkBusy] = useState(false);

  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (debouncedQ) p.set("q", debouncedQ);
    if (filter === "founding") p.set("founding", "true");
    if (filter === "non_founding") p.set("founding", "false");
    if (filter === "inactive") p.set("active", "false");
    p.set("skip", String(skip));
    p.set("limit", String(PAGE_SIZE));
    return p.toString();
  }, [debouncedQ, filter, skip]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/ops/users?${queryParams}`);
      setData(data);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error(formatApiError(e.response?.data?.detail) || "Failed to load customers");
    } finally { setLoading(false); }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 0 whenever filters change
  useEffect(() => { setSkip(0); }, [debouncedQ, filter]);

  // Clear selection whenever the visible dataset changes
  useEffect(() => { setSelectedIds(new Set()); }, [debouncedQ, filter, skip]);

  const toggleFounding = async (u) => {
    setBusy(u.id);
    const next = !u.is_founding_rider;
    // Optimistic
    setData((d) => ({ ...d, users: d.users.map((x) => x.id === u.id ? { ...x, is_founding_rider: next } : x) }));
    try {
      await api.post(`/admin/ops/users/${u.id}/founding-rider`, { is_founding_rider: next });
      toast.success(next ? `${u.name} is now a founding rider · legacy pricing` : `${u.name} moved to current pricing`);
    } catch (e) {
      // Revert on failure
      setData((d) => ({ ...d, users: d.users.map((x) => x.id === u.id ? { ...x, is_founding_rider: !next } : x) }));
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(null); }
  };

  const toggleActive = async (u) => {
    setBusy(u.id);
    const next = !u.is_active;
    setData((d) => ({ ...d, users: d.users.map((x) => x.id === u.id ? { ...x, is_active: next } : x) }));
    try {
      await api.post(`/admin/ops/users/${u.id}/active`, { is_active: next });
      toast.success(next ? `${u.name} reactivated` : `${u.name} deactivated — new bookings blocked`);
    } catch (e) {
      setData((d) => ({ ...d, users: d.users.map((x) => x.id === u.id ? { ...x, is_active: !next } : x) }));
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(null); }
  };

  const toggleDriver = async (u) => {
    setBusy(u.id);
    const next = !u.is_driver;
    setData((d) => ({ ...d, users: d.users.map((x) => x.id === u.id ? { ...x, is_driver: next } : x) }));
    try {
      await api.post(`/admin/users/${u.id}/driver`, { is_driver: next });
      toast.success(next ? `${u.name} promoted to driver` : `${u.name} demoted from driver`);
    } catch (e) {
      setData((d) => ({ ...d, users: d.users.map((x) => x.id === u.id ? { ...x, is_driver: !next } : x) }));
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(null); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const openConfirmSingle = (u) => setConfirmDelete({ users: [u] });
  const openConfirmBulk = () => {
    const users = data.users.filter((u) => selectedIds.has(u.id));
    setConfirmDelete({ users });
  };
  const performDelete = async () => {
    if (!confirmDelete) return;
    const ids = confirmDelete.users.map((u) => u.id);
    setBulkBusy(true);
    try {
      if (ids.length === 1) {
        const { data: res } = await api.delete(`/admin/ops/users/${ids[0]}`);
        toast.success(`${confirmDelete.users[0].name} deleted · ${res.routes} routes · ${res.trips} trips removed`);
      } else {
        const { data: res } = await api.post(`/admin/ops/users/bulk-delete`, { user_ids: ids });
        toast.success(`Deleted ${res.users} user${res.users === 1 ? "" : "s"} · ${res.routes} routes · ${res.trips} trips`);
      }
      clearSelection();
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  if (forbidden) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center" data-testid="customers-forbidden">
        <ShieldAlert size={28} className="mx-auto text-[#ed6500] mb-4" strokeWidth={1.6} />
        <div className="font-serif text-2xl mb-2">Not permitted</div>
        <p className="text-sm text-[#515E7B]">This dashboard is restricted to the Link operations account.</p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;
  const pageStart = data.total === 0 ? 0 : skip + 1;
  const pageEnd = Math.min(skip + (data.users?.length || 0), data.total || 0);

  return (
    <>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="admin-customers">
        <header className="pb-5 mb-6 border-b hairline">
          <div className="label-xs text-[#ed6500]">Customers</div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-none mt-2" data-testid="customers-title">
            {loading ? "—" : data.total} {data.total === 1 ? "rider" : "riders"}
          </h1>
        </header>

        <div className="flex items-center gap-2 mb-4 sm:mb-5">
          <div className="relative flex-1">
            <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3 text-[#515E7B]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border hairline focus:outline-none focus:border-[#ed6500]"
              data-testid="customers-search"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="customers-filter-bar">
          {[
            { id: "all",          label: "All" },
            { id: "founding",     label: "Founding only" },
            { id: "non_founding", label: "Current pricing" },
            { id: "inactive",     label: "Inactive" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-[10px] tracking-widest uppercase border hairline transition-colors ${
                filter === f.id ? "bg-[#ed6500] text-white border-[#ed6500]" : "bg-white text-[#515E7B] hover:text-[#0A1128]"
              }`}
              data-testid={`customers-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Selection toolbar */}
        {!loading && data.users.length > 0 && (
          <div className="mb-4 flex items-center justify-between gap-3" data-testid="customers-selection-toolbar">
            <label className="inline-flex items-center gap-2 text-[11px] tracking-widest uppercase text-[#515E7B] cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[#ed6500]"
                checked={data.users.length > 0 && data.users.every((u) => selectedIds.has(u.id))}
                onChange={(e) => {
                  if (e.target.checked) setSelectedIds(new Set(data.users.map((u) => u.id)));
                  else clearSelection();
                }}
                data-testid="customers-select-all"
              />
              Select all on this page
            </label>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#515E7B]" data-testid="customers-loading">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : data.users.length === 0 ? (
          <div className="border hairline bg-white p-8 text-center text-sm text-[#515E7B]" data-testid="customers-empty">
            No riders match this view.
          </div>
        ) : (
          <div className="space-y-3" data-testid="customers-list">
            {data.users.map((u) => (
              <CustomerCard
                key={u.id}
                user={u}
                busy={busy === u.id}
                selected={selectedIds.has(u.id)}
                onSelect={toggleSelect}
                onToggleFounding={() => toggleFounding(u)}
                onToggleActive={() => toggleActive(u)}
                onToggleDriver={() => toggleDriver(u)}
                onDelete={() => openConfirmSingle(u)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {data.total > PAGE_SIZE && (
          <div className="mt-6 flex items-center justify-between gap-3 text-xs text-[#515E7B]" data-testid="customers-pagination">
            <div>
              {pageStart}–{pageEnd} of {data.total} · page {currentPage} / {totalPages}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                disabled={skip === 0 || loading}
                className="p-2 border hairline bg-white text-[#515E7B] hover:text-[#ed6500] disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Previous page"
                data-testid="customers-prev-page"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setSkip(skip + PAGE_SIZE)}
                disabled={pageEnd >= data.total || loading}
                className="p-2 border hairline bg-white text-[#515E7B] hover:text-[#ed6500] disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Next page"
                data-testid="customers-next-page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Reserve space for sticky bar */}
        {selectedIds.size > 0 && <div className="h-20" aria-hidden="true" />}
      </div>

      {/* Sticky bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A1128] text-[#F9F7F4] border-t border-[#ed6500]"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          data-testid="customers-bulk-action-bar"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div className="text-xs">
              <span className="font-semibold" data-testid="customers-bulk-count">{selectedIds.size}</span>
              <span className="text-[#F9F7F4]/70"> selected</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearSelection}
                disabled={bulkBusy}
                className="text-[10px] tracking-widest uppercase text-[#F9F7F4]/70 hover:text-[#F9F7F4] px-3 py-2 disabled:opacity-50"
                data-testid="customers-bulk-clear"
              >
                Clear
              </button>
              <button
                onClick={openConfirmBulk}
                disabled={bulkBusy}
                className="text-[10px] tracking-widest uppercase font-semibold bg-[#ed6500] text-white px-4 py-2 hover:bg-[#d65a00] disabled:opacity-50 inline-flex items-center gap-2 transition-colors"
                data-testid="customers-bulk-delete"
              >
                <Trash2 size={12} /> Delete selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmDelete && (
        <DeleteConfirmModal
          users={confirmDelete.users}
          busy={bulkBusy}
          onCancel={() => !bulkBusy && setConfirmDelete(null)}
          onConfirm={performDelete}
        />
      )}
    </>
  );
}

function DeleteConfirmModal({ users, busy, onCancel, onConfirm }) {
  const single = users.length === 1 ? users[0] : null;
  const totalRides = users.reduce((s, u) => s + (u.total_rides || 0), 0);
  const totalActive = users.reduce((s, u) => s + (u.active_routes || 0), 0);
  // Heuristic: any user who's used the welcome offer has at least one paid tx.
  const paidUsers = users.filter((u) => u.first_ride_discount_used);
  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A1128]/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onCancel}
      data-testid="customers-confirm-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[#F9F7F4] border-t-2 sm:border border-[#ed6500] sm:rounded-none p-6 sm:p-8"
        style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
      >
        <div className="label-xs text-[#ed6500] mb-2">Confirm delete</div>
        <h2 className="font-serif text-2xl leading-tight text-[#0A1128]" data-testid="customers-confirm-title">
          {single ? `Delete ${single.name}?` : `Delete ${users.length} users?`}
        </h2>

        {single ? (
          <div className="mt-3 text-xs text-[#515E7B]" data-testid="customers-confirm-single-meta">
            <div className="flex items-center gap-1.5"><Mail size={11} /> {single.email}</div>
            {single.phone && <div className="flex items-center gap-1.5 mt-0.5"><Phone size={11} /> {single.phone}</div>}
            <div className="mt-2">
              <span className="text-[#0A1128] font-semibold">{single.total_rides || 0}</span> rides ·{" "}
              <span className="text-[#0A1128] font-semibold">{single.active_routes || 0}</span> active routes
            </div>
          </div>
        ) : (
          <div className="mt-3 text-xs text-[#515E7B]">
            Cumulative: <span className="text-[#0A1128] font-semibold">{totalRides}</span> rides ·{" "}
            <span className="text-[#0A1128] font-semibold">{totalActive}</span> active routes across {users.length} accounts.
          </div>
        )}

        {paidUsers.length > 0 && (
          <div className="mt-4 border-l-2 border-[#ed6500] bg-white p-3 flex items-start gap-2 text-xs text-[#515E7B]" data-testid="customers-confirm-paid-warning">
            <AlertTriangle size={14} className="text-[#ed6500] shrink-0 mt-0.5" />
            <span>
              <span className="text-[#0A1128] font-semibold">
                {paidUsers.length} {paidUsers.length === 1 ? "user has" : "users have"}
              </span> completed payments on file. Deleting removes routes, trips, transactions, subscriptions, and pricing snapshots. Stripe charges remain on the customer's statement.
            </span>
          </div>
        )}

        <p className="mt-4 text-sm text-[#515E7B]">This cascade is permanent and cannot be undone.</p>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-[10px] tracking-widest uppercase text-[#515E7B] hover:text-[#0A1128] px-4 py-2.5 disabled:opacity-50"
            data-testid="customers-confirm-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="text-[10px] tracking-widest uppercase font-semibold bg-[#ed6500] text-white px-5 py-2.5 hover:bg-[#d65a00] disabled:opacity-50 inline-flex items-center gap-2 transition-colors"
            data-testid="customers-confirm-delete"
          >
            <Trash2 size={12} /> {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomerCard({ user: u, busy, selected = false, onSelect, onToggleFounding, onToggleActive, onToggleDriver, onDelete }) {
  const created = u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
  const isAdmin = u.role === "admin";
  const isOpsAdmin = !!u.is_ops_admin;
  return (
    <div
      className={`bg-white border hairline p-4 sm:p-5 ${u.is_active === false ? "opacity-60" : ""} ${selected ? "ring-1 ring-[#ed6500]" : ""}`}
      data-testid={`customer-row-${u.id}`}
    >
      <div className="flex items-start gap-3">
        {onSelect && !isOpsAdmin && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(u.id)}
            className="mt-1 w-4 h-4 accent-[#ed6500] shrink-0 cursor-pointer"
            aria-label={`Select ${u.name}`}
            data-testid={`customer-select-${u.id}`}
          />
        )}
        <div className="flex items-start justify-between gap-3 flex-wrap sm:flex-nowrap flex-1 min-w-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-serif text-lg sm:text-xl truncate" data-testid={`customer-name-${u.id}`}>{u.name}</div>
            {isAdmin && (
              <span className="text-[9px] font-semibold tracking-widest uppercase text-[#ed6500] border border-[#ed6500]/40 px-1.5 py-0.5">
                Admin
              </span>
            )}
            {u.is_founding_rider && (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold tracking-widest uppercase text-[#ed6500]" data-testid={`customer-founding-badge-${u.id}`}>
                <Crown size={10} /> Founding
              </span>
            )}
            {u.is_active === false && (
              <span className="text-[9px] font-semibold tracking-widest uppercase text-[#515E7B] border border-[#515E7B]/40 px-1.5 py-0.5" data-testid={`customer-inactive-badge-${u.id}`}>
                Inactive
              </span>
            )}
            {u.first_ride_discount_used === false && (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold tracking-widest uppercase text-[#5c97f7]">
                <Sparkles size={10} /> First ride
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-[#515E7B] flex items-center gap-1.5 truncate">
            <Mail size={10} /> <span className="truncate">{u.email}</span>
          </div>
          {u.phone && (
            <div className="mt-0.5 text-xs text-[#515E7B] flex items-center gap-1.5">
              <Phone size={10} /> {u.phone}
            </div>
          )}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-[#515E7B] flex-wrap">
            <span className="inline-flex items-center gap-1"><CalendarDays size={10} /> Joined {created}</span>
            <span><span className="text-[#0A1128] font-semibold">{u.total_rides}</span> rides</span>
            {u.active_routes > 0 && <span><span className="text-[#0A1128] font-semibold">{u.active_routes}</span> active</span>}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 shrink-0 w-full sm:w-auto">
          <button
            onClick={onToggleFounding}
            disabled={busy}
            className={`px-3 py-2 text-[10px] tracking-widest uppercase font-semibold border hairline transition-colors disabled:opacity-50 rounded-full ${
              u.is_founding_rider ? "bg-[#ed6500] text-white border-[#ed6500]" : "bg-white text-[#515E7B] hover:text-[#ed6500]"
            }`}
            title={u.is_founding_rider ? "Remove founding pricing" : "Grant founding pricing (legacy rates)"}
            data-testid={`customer-founding-toggle-${u.id}`}
          >
            <Crown size={11} className="inline -mt-0.5 mr-1" />
            {u.is_founding_rider ? "Founding" : "Founding off"}
          </button>
          <button
            onClick={onToggleDriver}
            disabled={busy}
            className={`px-3 py-2 text-[10px] tracking-widest uppercase font-semibold border hairline transition-colors disabled:opacity-50 rounded-full ${
              u.is_driver ? "bg-[#0074d4] text-white border-[#0074d4]" : "bg-white text-[#515E7B] hover:text-[#0074d4]"
            }`}
            title={u.is_driver ? "Remove driver role" : "Grant driver role (driver app access)"}
            data-testid={`customer-driver-toggle-${u.id}`}
          >
            <Car size={11} className="inline -mt-0.5 mr-1" />
            {u.is_driver ? "Driver" : "Driver off"}
          </button>
          <button
            onClick={onToggleActive}
            disabled={busy}
            className={`px-3 py-2 text-[10px] tracking-widest uppercase font-semibold border hairline transition-colors disabled:opacity-50 ${
              u.is_active === false ? "bg-[#515E7B] text-white border-[#515E7B]" : "bg-white text-[#515E7B] hover:text-[#0A1128]"
            }`}
            title={u.is_active === false ? "Reactivate user (allow new bookings)" : "Deactivate user (block new bookings)"}
            data-testid={`customer-active-toggle-${u.id}`}
          >
            {u.is_active === false ? <><UserX size={11} className="inline -mt-0.5 mr-1" /> Inactive</> : <><UserCheck size={11} className="inline -mt-0.5 mr-1" /> Active</>}
          </button>
          <Link
            to={`/admin/ops?user=${u.id}`}
            state={{ rider_name: u.name }}
            className="px-3 py-2 text-[10px] tracking-widest uppercase font-semibold border hairline bg-white text-[#5c97f7] hover:bg-[#5c97f7] hover:text-white inline-flex items-center justify-center gap-1.5 transition-colors"
            data-testid={`customer-view-rides-${u.id}`}
          >
            View rides <ArrowRight size={11} />
          </Link>
          {!isOpsAdmin && onDelete && (
            <button
              onClick={onDelete}
              disabled={busy}
              className="px-3 py-2 text-[10px] tracking-widest uppercase font-semibold border hairline bg-white text-[#ed6500] hover:bg-[#ed6500] hover:text-white inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
              title="Delete user (cascade)"
              data-testid={`customer-delete-${u.id}`}
            >
              <Trash2 size={11} /> Delete
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
